import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeftIcon, UserIcon, BarChart2Icon, MessageSquareIcon, FileTextIcon, GlobeIcon, LinkedinIcon, GithubIcon, TwitterIcon, PaperclipIcon, PhoneIcon, MailIcon, MapPinIcon, CalendarIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
export const CandidateDetail = () => {
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  // Mock data for a candidate
  const candidate = {
    id: parseInt(id),
    name: 'Sarah Johnson',
    position: 'Senior Developer',
    status: 'Completed',
    score: 92,
    email: 'sarah.johnson@example.com',
    phone: '+1 (555) 123-4567',
    location: 'San Francisco, CA',
    appliedDate: '2023-06-15',
    source: 'LinkedIn',
    botRisk: 'Low',
    skills: ['React', 'TypeScript', 'Node.js', 'GraphQL', 'AWS', 'Docker'],
    education: [{
      degree: 'M.S. Computer Science',
      school: 'Stanford University',
      year: '2018-2020'
    }, {
      degree: 'B.S. Computer Science',
      school: 'UC Berkeley',
      year: '2014-2018'
    }],
    experience: [{
      title: 'Senior Frontend Developer',
      company: 'Tech Innovations Inc.',
      duration: '2020-Present',
      description: "Led development of the company's flagship product, improving performance by 40%."
    }, {
      title: 'Frontend Developer',
      company: 'WebSolutions Co.',
      duration: '2018-2020',
      description: 'Developed responsive web applications using React and TypeScript.'
    }],
    screeningResults: {
      technicalSkills: 95,
      communication: 88,
      problemSolving: 90,
      teamwork: 85,
      leadershipPotential: 82
    },
    aiInsights: ['Strong technical background with expertise in modern frontend technologies', 'Excellent communication skills demonstrated during the screening', 'Shows leadership potential based on past project management experience', 'Good cultural fit with emphasis on collaborative work', 'Demonstrated ability to solve complex problems efficiently'],
    conversationHighlights: [{
      question: 'Describe a challenging project you worked on recently.',
      answer: 'I led a team of 5 developers to rebuild our payment processing system, which was experiencing performance issues with high transaction volumes. We redesigned the architecture using microservices and implemented efficient caching strategies, resulting in a 60% improvement in processing speed and 99.99% uptime.',
      analysis: 'Demonstrates strong technical leadership and problem-solving abilities.'
    }, {
      question: 'How do you approach learning new technologies?',
      answer: "I maintain a structured approach to learning new technologies. I start by understanding the core concepts and use cases, then build small projects to apply what I've learned. I also participate in open-source projects and tech communities to gain real-world experience and feedback.",
      analysis: 'Shows methodical learning approach and commitment to continuous improvement.'
    }]
  };
  return <div data-id="element-417">
      <div className="mb-6" data-id="element-418">
        <Link to="/dashboard/candidates" className="inline-flex items-center text-indigo-600 hover:text-indigo-800" data-id="element-419">
          <ChevronLeftIcon size={16} className="mr-1" data-id="element-420" />
          Back to Candidates
        </Link>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-421">
        <div className="p-6 border-b border-gray-200" data-id="element-422">
          <div className="flex flex-col md:flex-row md:items-center justify-between" data-id="element-423">
            <div className="flex items-center" data-id="element-424">
              <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xl font-bold" data-id="element-425">
                {candidate.name.charAt(0)}
              </div>
              <div className="ml-4" data-id="element-426">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-427">
                  {candidate.name}
                </h1>
                <div className="flex items-center mt-1" data-id="element-428">
                  <span className="text-gray-600 mr-3" data-id="element-429">
                    {candidate.position}
                  </span>
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                      ${candidate.status === 'Completed' ? 'bg-green-100 text-green-800' : candidate.status === 'In Progress' ? 'bg-blue-100 text-blue-800' : candidate.status === 'Scheduled' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-430">
                    {candidate.status}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4 md:mt-0 flex flex-wrap gap-2" data-id="element-431">
              <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-432">
                <MessageSquareIcon size={16} className="mr-2" data-id="element-433" />
                Message
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-434">
                <CalendarIcon size={16} className="mr-2" data-id="element-435" />
                Schedule Interview
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-436">
                More Actions
              </button>
            </div>
          </div>
          <div className="mt-6 border-t border-gray-100 pt-4" data-id="element-437">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-438">
              <div className="flex items-center" data-id="element-439">
                <MailIcon size={16} className="text-gray-400 mr-2" data-id="element-440" />
                <span className="text-sm text-gray-600" data-id="element-441">{candidate.email}</span>
              </div>
              <div className="flex items-center" data-id="element-442">
                <PhoneIcon size={16} className="text-gray-400 mr-2" data-id="element-443" />
                <span className="text-sm text-gray-600" data-id="element-444">{candidate.phone}</span>
              </div>
              <div className="flex items-center" data-id="element-445">
                <MapPinIcon size={16} className="text-gray-400 mr-2" data-id="element-446" />
                <span className="text-sm text-gray-600" data-id="element-447">
                  {candidate.location}
                </span>
              </div>
              <div className="flex items-center" data-id="element-448">
                <div className="flex items-center" data-id="element-449">
                  {candidate.botRisk === 'Low' && <CheckCircleIcon size={16} className="text-green-500 mr-1" data-id="element-450" />}
                  {candidate.botRisk === 'Medium' && <AlertCircleIcon size={16} className="text-yellow-500 mr-1" data-id="element-451" />}
                  {candidate.botRisk === 'High' && <XCircleIcon size={16} className="text-red-500 mr-1" data-id="element-452" />}
                  <span className={`text-sm ${candidate.botRisk === 'Low' ? 'text-green-600' : candidate.botRisk === 'Medium' ? 'text-yellow-600' : 'text-red-600'}`} data-id="element-453">
                    Bot Risk: {candidate.botRisk}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="border-b border-gray-200" data-id="element-454">
          <nav className="flex -mb-px" data-id="element-455">
            {[{
            id: 'overview',
            label: 'Overview',
            icon: <UserIcon size={16} data-id="element-456" />
          }, {
            id: 'screening',
            label: 'Screening Results',
            icon: <BarChart2Icon size={16} data-id="element-457" />
          }, {
            id: 'conversation',
            label: 'Conversation',
            icon: <MessageSquareIcon size={16} data-id="element-458" />
          }, {
            id: 'resume',
            label: 'Resume',
            icon: <FileTextIcon size={16} data-id="element-459" />
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center py-4 px-6 border-b-2 font-medium text-sm ${activeTab === tab.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-460">
                {tab.icon}
                <span className="ml-2" data-id="element-461">{tab.label}</span>
              </button>)}
          </nav>
        </div>
        <div className="p-6" data-id="element-462">
          {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-463">
              <div className="lg:col-span-2 space-y-6" data-id="element-464">
                <div data-id="element-465">
                  <h2 className="text-lg font-medium mb-4" data-id="element-466">AI Insights</h2>
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100" data-id="element-467">
                    <ul className="space-y-2" data-id="element-468">
                      {candidate.aiInsights.map((insight, index) => <li key={index} className="flex items-start" data-id="element-469">
                          <span className="h-5 w-5 flex-shrink-0 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-600 mr-2 mt-0.5" data-id="element-470">
                            ✓
                          </span>
                          <span className="text-gray-800" data-id="element-471">{insight}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
                <div data-id="element-472">
                  <h2 className="text-lg font-medium mb-4" data-id="element-473">Experience</h2>
                  <div className="space-y-4" data-id="element-474">
                    {candidate.experience.map((exp, index) => <div key={index} className="border border-gray-200 rounded-lg p-4" data-id="element-475">
                        <div className="flex justify-between" data-id="element-476">
                          <div data-id="element-477">
                            <h3 className="font-medium" data-id="element-478">{exp.title}</h3>
                            <p className="text-sm text-gray-600" data-id="element-479">
                              {exp.company}
                            </p>
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-480">
                            {exp.duration}
                          </div>
                        </div>
                        <p className="mt-2 text-sm text-gray-600" data-id="element-481">
                          {exp.description}
                        </p>
                      </div>)}
                  </div>
                </div>
                <div data-id="element-482">
                  <h2 className="text-lg font-medium mb-4" data-id="element-483">Education</h2>
                  <div className="space-y-4" data-id="element-484">
                    {candidate.education.map((edu, index) => <div key={index} className="border border-gray-200 rounded-lg p-4" data-id="element-485">
                        <div className="flex justify-between" data-id="element-486">
                          <div data-id="element-487">
                            <h3 className="font-medium" data-id="element-488">{edu.degree}</h3>
                            <p className="text-sm text-gray-600" data-id="element-489">
                              {edu.school}
                            </p>
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-490">
                            {edu.year}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
              <div className="space-y-6" data-id="element-491">
                <div data-id="element-492">
                  <h2 className="text-lg font-medium mb-4" data-id="element-493">Candidate Score</h2>
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-494">
                    <div className="flex items-center justify-between mb-4" data-id="element-495">
                      <span className="text-gray-600" data-id="element-496">Overall Score</span>
                      <span className="text-2xl font-bold text-indigo-600" data-id="element-497">
                        {candidate.score}/100
                      </span>
                    </div>
                    <div className="space-y-4" data-id="element-498">
                      {Object.entries(candidate.screeningResults).map(([key, value]) => <div key={key} data-id="element-499">
                            <div className="flex justify-between mb-1" data-id="element-500">
                              <span className="text-sm capitalize" data-id="element-501">
                                {key.replace(/([A-Z])/g, ' $1')}
                              </span>
                              <span className="text-sm text-gray-500" data-id="element-502">
                                {value}/100
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-503">
                              <div className={`h-2 rounded-full ${value >= 90 ? 'bg-green-600' : value >= 75 ? 'bg-indigo-600' : value >= 60 ? 'bg-yellow-600' : 'bg-red-600'}`} style={{
                        width: `${value}%`
                      }} data-id="element-504"></div>
                            </div>
                          </div>)}
                    </div>
                  </div>
                </div>
                <div data-id="element-505">
                  <h2 className="text-lg font-medium mb-4" data-id="element-506">Skills</h2>
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-507">
                    <div className="flex flex-wrap gap-2" data-id="element-508">
                      {candidate.skills.map((skill, index) => <span key={index} className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm" data-id="element-509">
                          {skill}
                        </span>)}
                    </div>
                  </div>
                </div>
                <div data-id="element-510">
                  <h2 className="text-lg font-medium mb-4" data-id="element-511">
                    Additional Information
                  </h2>
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-512">
                    <div className="space-y-3" data-id="element-513">
                      <div className="flex justify-between" data-id="element-514">
                        <span className="text-gray-600" data-id="element-515">Applied via</span>
                        <span className="font-medium" data-id="element-516">{candidate.source}</span>
                      </div>
                      <div className="flex justify-between" data-id="element-517">
                        <span className="text-gray-600" data-id="element-518">Application Date</span>
                        <span className="font-medium" data-id="element-519">
                          {new Date(candidate.appliedDate).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="pt-3 border-t border-gray-100" data-id="element-520">
                        <h3 className="text-sm font-medium mb-2" data-id="element-521">
                          Social Profiles
                        </h3>
                        <div className="flex space-x-3" data-id="element-522">
                          <a href="#" className="text-gray-500 hover:text-indigo-600" data-id="element-523">
                            <LinkedinIcon size={20} data-id="element-524" />
                          </a>
                          <a href="#" className="text-gray-500 hover:text-indigo-600" data-id="element-525">
                            <GithubIcon size={20} data-id="element-526" />
                          </a>
                          <a href="#" className="text-gray-500 hover:text-indigo-600" data-id="element-527">
                            <TwitterIcon size={20} data-id="element-528" />
                          </a>
                          <a href="#" className="text-gray-500 hover:text-indigo-600" data-id="element-529">
                            <GlobeIcon size={20} data-id="element-530" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {activeTab === 'screening' && <div data-id="element-531">
              <div className="mb-6" data-id="element-532">
                <h2 className="text-lg font-medium mb-4" data-id="element-533">Screening Summary</h2>
                <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-534">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-535">
                    <div data-id="element-536">
                      <div className="text-center mb-4" data-id="element-537">
                        <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-indigo-100 text-indigo-600 text-2xl font-bold" data-id="element-538">
                          {candidate.score}
                        </div>
                        <h3 className="mt-2 font-medium" data-id="element-539">Overall Score</h3>
                      </div>
                      <div className="space-y-4 mt-6" data-id="element-540">
                        {Object.entries(candidate.screeningResults).map(([key, value]) => <div key={key} data-id="element-541">
                              <div className="flex justify-between mb-1" data-id="element-542">
                                <span className="text-sm capitalize" data-id="element-543">
                                  {key.replace(/([A-Z])/g, ' $1')}
                                </span>
                                <span className="text-sm text-gray-500" data-id="element-544">
                                  {value}/100
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-545">
                                <div className={`h-2 rounded-full ${value >= 90 ? 'bg-green-600' : value >= 75 ? 'bg-indigo-600' : value >= 60 ? 'bg-yellow-600' : 'bg-red-600'}`} style={{
                          width: `${value}%`
                        }} data-id="element-546"></div>
                              </div>
                            </div>)}
                      </div>
                    </div>
                    <div data-id="element-547">
                      <h3 className="font-medium mb-3" data-id="element-548">Key Strengths</h3>
                      <ul className="space-y-2 mb-6" data-id="element-549">
                        <li className="flex items-start" data-id="element-550">
                          <CheckCircleIcon size={16} className="text-green-500 mr-2 mt-0.5" data-id="element-551" />
                          <span data-id="element-552">
                            Strong technical knowledge in required technologies
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-553">
                          <CheckCircleIcon size={16} className="text-green-500 mr-2 mt-0.5" data-id="element-554" />
                          <span data-id="element-555">
                            Excellent communication and articulation of complex
                            concepts
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-556">
                          <CheckCircleIcon size={16} className="text-green-500 mr-2 mt-0.5" data-id="element-557" />
                          <span data-id="element-558">
                            Demonstrated problem-solving abilities with real
                            examples
                          </span>
                        </li>
                      </ul>
                      <h3 className="font-medium mb-3" data-id="element-559">
                        Areas for Further Assessment
                      </h3>
                      <ul className="space-y-2" data-id="element-560">
                        <li className="flex items-start" data-id="element-561">
                          <AlertCircleIcon size={16} className="text-yellow-500 mr-2 mt-0.5" data-id="element-562" />
                          <span data-id="element-563">
                            Team leadership experience in larger organizations
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-564">
                          <AlertCircleIcon size={16} className="text-yellow-500 mr-2 mt-0.5" data-id="element-565" />
                          <span data-id="element-566">
                            Experience with specific industry regulations
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-567">
                <h2 className="text-lg font-medium mb-4" data-id="element-568">AI Analysis</h2>
                <div className="bg-indigo-50 rounded-lg p-6 border border-indigo-100" data-id="element-569">
                  <p className="text-gray-800 mb-4" data-id="element-570">
                    Based on the screening conversation, Sarah demonstrates
                    strong technical proficiency in frontend development with
                    React and TypeScript. Her communication skills are
                    excellent, and she articulates complex technical concepts
                    clearly. She has shown leadership potential through her
                    experience managing small teams and complex projects.
                  </p>
                  <p className="text-gray-800 mb-4" data-id="element-571">
                    Her problem-solving approach is methodical and
                    results-oriented, as evidenced by her description of
                    optimizing the payment processing system at her previous
                    company. She shows a good cultural fit with your
                    organization's values, particularly in her emphasis on
                    collaborative work and continuous learning.
                  </p>
                  <p className="text-gray-800" data-id="element-572">
                    <strong data-id="element-573">Recommendation:</strong> Sarah is a strong candidate
                    for the Senior Developer position. Consider proceeding to
                    the technical interview stage to further assess her coding
                    abilities and system design knowledge.
                  </p>
                </div>
              </div>
            </div>}
          {activeTab === 'conversation' && <div data-id="element-574">
              <h2 className="text-lg font-medium mb-4" data-id="element-575">
                Screening Conversation
              </h2>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-576">
                <div className="mb-6 pb-4 border-b border-gray-100" data-id="element-577">
                  <div className="flex justify-between text-sm text-gray-500 mb-2" data-id="element-578">
                    <span data-id="element-579">
                      Screening conducted on{' '}
                      {new Date(candidate.appliedDate).toLocaleDateString()}
                    </span>
                    <span data-id="element-580">Duration: 32 minutes</span>
                  </div>
                  <div className="flex space-x-4" data-id="element-581">
                    <button className="text-indigo-600 text-sm font-medium" data-id="element-582">
                      Download Transcript
                    </button>
                    <button className="text-indigo-600 text-sm font-medium" data-id="element-583">
                      Listen to Recording
                    </button>
                  </div>
                </div>
                <div className="space-y-6" data-id="element-584">
                  {candidate.conversationHighlights.map((highlight, index) => <div key={index} className="space-y-3" data-id="element-585">
                      <div className="flex items-start" data-id="element-586">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 mr-3 mt-1" data-id="element-587">
                          AI
                        </div>
                        <div className="bg-gray-100 rounded-lg p-3 flex-1" data-id="element-588">
                          <p className="text-gray-800" data-id="element-589">{highlight.question}</p>
                        </div>
                      </div>
                      <div className="flex items-start" data-id="element-590">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-3 mt-1" data-id="element-591">
                          {candidate.name.charAt(0)}
                        </div>
                        <div className="bg-indigo-50 rounded-lg p-3 flex-1" data-id="element-592">
                          <p className="text-gray-800" data-id="element-593">{highlight.answer}</p>
                        </div>
                      </div>
                      <div className="ml-11 bg-green-50 border border-green-100 rounded-lg p-3" data-id="element-594">
                        <p className="text-sm text-green-800" data-id="element-595">
                          <strong data-id="element-596">AI Analysis:</strong> {highlight.analysis}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>}
          {activeTab === 'resume' && <div data-id="element-597">
              <div className="flex justify-between items-center mb-4" data-id="element-598">
                <h2 className="text-lg font-medium" data-id="element-599">Resume</h2>
                <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-600">
                  <PaperclipIcon size={16} className="mr-1" data-id="element-601" />
                  Download Resume
                </button>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-602">
                <div className="border-b border-gray-100 pb-4 mb-4" data-id="element-603">
                  <h1 className="text-xl font-bold text-gray-900 mb-1" data-id="element-604">
                    {candidate.name}
                  </h1>
                  <p className="text-gray-600" data-id="element-605">{candidate.position}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2 text-sm text-gray-600" data-id="element-606">
                    <div className="flex items-center" data-id="element-607">
                      <MailIcon size={14} className="mr-1" data-id="element-608" />
                      {candidate.email}
                    </div>
                    <div className="flex items-center" data-id="element-609">
                      <PhoneIcon size={14} className="mr-1" data-id="element-610" />
                      {candidate.phone}
                    </div>
                    <div className="flex items-center" data-id="element-611">
                      <MapPinIcon size={14} className="mr-1" data-id="element-612" />
                      {candidate.location}
                    </div>
                  </div>
                </div>
                <div className="mb-6" data-id="element-613">
                  <h2 className="text-lg font-medium mb-3" data-id="element-614">Summary</h2>
                  <p className="text-gray-700" data-id="element-615">
                    Senior Developer with over 5 years of experience in frontend
                    development, specializing in React and TypeScript. Proven
                    track record of delivering high-performance web applications
                    and leading development teams. Strong problem-solving
                    abilities and excellent communication skills.
                  </p>
                </div>
                <div className="mb-6" data-id="element-616">
                  <h2 className="text-lg font-medium mb-3" data-id="element-617">Experience</h2>
                  <div className="space-y-4" data-id="element-618">
                    {candidate.experience.map((exp, index) => <div key={index} className="border-l-2 border-gray-200 pl-4" data-id="element-619">
                        <div className="flex justify-between" data-id="element-620">
                          <h3 className="font-medium" data-id="element-621">{exp.title}</h3>
                          <span className="text-sm text-gray-500" data-id="element-622">
                            {exp.duration}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1" data-id="element-623">
                          {exp.company}
                        </p>
                        <p className="text-sm text-gray-700" data-id="element-624">
                          {exp.description}
                        </p>
                        {index === 0 && <ul className="mt-2 list-disc list-inside text-sm text-gray-700 space-y-1" data-id="element-625">
                            <li data-id="element-626">
                              Led a team of 5 developers in rebuilding the
                              payment processing system
                            </li>
                            <li data-id="element-627">
                              Improved application performance by 40% through
                              code optimization
                            </li>
                            <li data-id="element-628">
                              Implemented CI/CD pipeline reducing deployment
                              time by 60%
                            </li>
                            <li data-id="element-629">
                              Mentored junior developers and conducted code
                              reviews
                            </li>
                          </ul>}
                      </div>)}
                  </div>
                </div>
                <div className="mb-6" data-id="element-630">
                  <h2 className="text-lg font-medium mb-3" data-id="element-631">Education</h2>
                  <div className="space-y-4" data-id="element-632">
                    {candidate.education.map((edu, index) => <div key={index} className="border-l-2 border-gray-200 pl-4" data-id="element-633">
                        <div className="flex justify-between" data-id="element-634">
                          <h3 className="font-medium" data-id="element-635">{edu.degree}</h3>
                          <span className="text-sm text-gray-500" data-id="element-636">
                            {edu.year}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600" data-id="element-637">{edu.school}</p>
                      </div>)}
                  </div>
                </div>
                <div data-id="element-638">
                  <h2 className="text-lg font-medium mb-3" data-id="element-639">Skills</h2>
                  <div className="flex flex-wrap gap-2" data-id="element-640">
                    {candidate.skills.map((skill, index) => <span key={index} className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm" data-id="element-641">
                        {skill}
                      </span>)}
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};